import React, { useEffect, useMemo, useState } from 'react';
import { buildApiUrl } from '../lib/api';
import { resolveUserIdentity } from '../utils/userIdentity';
import { DEFAULT_SCORE_ARTIST, DEFAULT_SCORE_TITLE } from '../utils/scoreDefaults';
import useLocale from '../hooks/useLocale';
import '../styles/Navigation.css';

export default function AppSidebar({ user, overlay = false, isOpen = true, bottomInset = 0 }) {
  const locale = useLocale();
  const { userId } = resolveUserIdentity(user);
  const copy = locale === 'ja' ? {
    newScore: '新しい曲', libraryLoading: '読み込み中...',
    libraryEmpty: '保存済みの曲はありません', libraryError: '曲を読み込めませんでした', libraryLogin: 'ログインすると曲を表示できます',
  } : {
    newScore: 'New score', libraryLoading: 'Loading...',
    libraryEmpty: 'No saved scores', libraryError: 'Could not load the library', libraryLogin: 'Log in to view your scores',
  };
  const [libraryScores, setLibraryScores] = useState([]);
  const [libraryStatus, setLibraryStatus] = useState('idle');
  const [expandedLibraryArtist, setExpandedLibraryArtist] = useState(null);
  const libraryArtists = useMemo(() => {
    const grouped = new Map();
    libraryScores.forEach((score) => {
      const artistName = (typeof score.artist === 'string' ? score.artist.trim() : '') || DEFAULT_SCORE_ARTIST;
      const entries = grouped.get(artistName) || [];
      entries.push(score);
      grouped.set(artistName, entries);
    });
    return [...grouped.entries()].sort(([left], [right]) => left.localeCompare(right, 'ja'));
  }, [libraryScores]);

  useEffect(() => {
    if (!isOpen || !userId) return;
    let cancelled = false;
    setLibraryStatus('loading');
    fetch(`${buildApiUrl('/get-scores')}?userId=${encodeURIComponent(userId)}`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Failed to load library')))
      .then((data) => {
        if (cancelled) return;
        setLibraryScores(Array.isArray(data.scores) ? data.scores : (Array.isArray(data) ? data : []));
        setLibraryStatus('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load score library', error);
        setLibraryStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, userId]);

  return (
        <aside id="app-navigation" className={`degreebook-sidebar${overlay ? " degreebook-sidebar--overlay" : ""}${isOpen ? " is-open" : ""}`} style={overlay ? { bottom: bottomInset } : undefined} inert={!isOpen} aria-hidden={!isOpen} aria-label={locale === "ja" ? "メインメニュー" : "Main menu"}>
          <nav className="degreebook-nav">
              <div className="degreebook-library-tree">
                {libraryArtists.map(([artistName, scores]) => (
                  <div className="degreebook-library-artist" key={artistName}>
                    <button
                      type="button"
                      onClick={() => setExpandedLibraryArtist((artist) => artist === artistName ? null : artistName)}
                      aria-expanded={expandedLibraryArtist === artistName}
                    >
                      <span>{expandedLibraryArtist === artistName ? '⌄' : '›'} {artistName}</span>
                      <small>{scores.length}</small>
                    </button>
                    {expandedLibraryArtist === artistName && scores.map((score) => (
                      <button
                        type="button"
                        className="degreebook-library-song"
                        key={score.scoreId}
                        onClick={() => { window.location.href = `/?scoreid=${encodeURIComponent(score.scoreId)}`; }}
                      >
                        {score.title || DEFAULT_SCORE_TITLE}
                      </button>
                    ))}
                  </div>
                ))}
                {libraryStatus === 'loading' && <span className="degreebook-library-empty">{copy.libraryLoading}</span>}
                {libraryStatus === 'error' && <span className="degreebook-library-empty degreebook-library-empty--error">{copy.libraryError}</span>}
                {libraryStatus === 'ready' && !libraryArtists.length && <span className="degreebook-library-empty">{copy.libraryEmpty}</span>}
                {libraryStatus === 'idle' && !userId && <span className="degreebook-library-empty">{copy.libraryLogin}</span>}
              </div>
          </nav>
        </aside>
  );
}
